import { lazy, Suspense, useState, useCallback } from 'react';
import { useAppStore } from '@/store';
import { RegulatoryMode } from '@allergen-lens/shared-logic';
import { useEngine } from '@/hooks/useEngine';
import { usePersistence } from '@/hooks/usePersistence';
import { ErrorBoundary } from './ErrorBoundary';
import { Header } from './Header';
import { StatusBar } from './StatusBar';
import { InputPanel } from './InputPanel';
import { ResultsPanel } from './ResultsPanel';
import { ProfilePanel } from './ProfilePanel';
import { TabGroup, SettingsDrawer } from 'calc-nav';
import { Select } from 'calc-controls';
import { AboutModal, buildAboutConfig } from '@ascend/about-modal';
import { IndexedDBBanner, LibraryLoadBanner } from './WarningBanner';
import { FeedbackForm, FeedbackButton } from './FeedbackForm/FeedbackForm';
import type { UserFeedback } from '@/utils/userFeedback';
import styles from './App.module.css';

const aboutConfig = buildAboutConfig(import.meta.env, 'VITE_APP_');

const ScanHistory = lazy(() => import('./ScanHistory').then((m) => ({ default: m.ScanHistory })));
const AuditLog = lazy(() => import('./AuditLog').then((m) => ({ default: m.AuditLog })));
const EmergencyCard = lazy(() => import('./EmergencyCard').then((m) => ({ default: m.EmergencyCard })));
const ERLocator = lazy(() => import('./ERLocator').then((m) => ({ default: m.ERLocator })));

function TabFallback() {
  return <div className={styles.loadingFallback}>Loading…</div>;
}

function TabErrorFallback({ tabName, onReport }: { tabName: string; onReport: () => void }) {
  return (
    <div className={styles.errorTab}>
      <p>Something went wrong in {tabName}.</p>
      <button className={styles.reportButton} onClick={onReport}>
        Report this error
      </button>
    </div>
  );
}

function AppInner() {
  useEngine();
  usePersistence();

  const settingsOpen = useAppStore((s) => s.settingsOpen);
  const setSettingsOpen = useAppStore((s) => s.setSettingsOpen);
  const mode = useAppStore((s) => s.mode);
  const setRegulatoryMode = useAppStore((s) => s.setRegulatoryMode);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [feedbackConfig, setFeedbackConfig] = useState<{
    type?: UserFeedback['type'];
    category?: string;
    error?: string;
  }>({});

  const handleOpenFeedback = useCallback((
    type?: UserFeedback['type'],
    category?: string,
    error?: string,
  ) => {
    const cfg: { type?: UserFeedback['type']; category?: string; error?: string } = {};
    if (type) cfg.type = type;
    if (category) cfg.category = category;
    if (error) cfg.error = error;
    setFeedbackConfig(cfg);
    setFeedbackOpen(true);
  }, []);

  const handleCloseFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackConfig({});
  }, []);

  return (
    <div className={styles.app}>
      <a href="#main-content" className="skip-link">Skip to content</a>
      <IndexedDBBanner />
      <LibraryLoadBanner />
      <header role="banner">
        <Header />
      </header>
      <main id="main-content" role="main" className={styles.content}>
        <section className={styles.coreGrid} aria-label="Core analysis">
          <div className={styles.inputCard}>
            <ErrorBoundary fallback={<TabErrorFallback tabName="Input" onReport={() => handleOpenFeedback('bug_report', 'error')} />}>
              <InputPanel />
            </ErrorBoundary>
          </div>
          <div className={styles.resultsCard}>
            <ErrorBoundary fallback={<TabErrorFallback tabName="Results" onReport={() => handleOpenFeedback('bug_report', 'error')} />}>
              <TabGroup
                section="results"
                ariaLabel="Results section"
                tabs={[
                  { id: 'results',     label: 'Results',     content: <ResultsPanel /> },
                  { id: 'history',     label: 'History',     content: <Suspense fallback={<TabFallback />}><ScanHistory /></Suspense> },
                  { id: 'emergency',   label: 'Emergency',   content: <Suspense fallback={<TabFallback />}><EmergencyCard /></Suspense> },
                  { id: 'er-locator',  label: 'ER Locator',  content: <Suspense fallback={<TabFallback />}><ERLocator /></Suspense> },
                ]}
              />
            </ErrorBoundary>
          </div>
        </section>
      </main>
      <footer role="contentinfo">
        <StatusBar />
      </footer>
      <SettingsDrawer isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} title="Settings">
        <div className={styles.settingsField}>
          <label htmlFor="reg-mode" className={styles.settingsLabel}>Regulatory Mode</label>
          <Select
            options={[
              { value: RegulatoryMode.EU, label: 'EU (14 allergens)' },
              { value: RegulatoryMode.US, label: 'US (9 allergens)' },
              { value: RegulatoryMode.EU_NON_PREPACKED, label: 'EU Non-Prepacked' },
            ]}
            value={mode}
            onChange={(v) => setRegulatoryMode(v as RegulatoryMode)}
          />
          <p className={styles.settingsHint}>
            Controls which allergens are checked. EU: 14 mandatory allergens (Regulation 1169/2011). US: 9 major allergens (FALCPA + FASTER Act). EU Non-Prepacked: reduced labelling rules per Art. 44.
          </p>
        </div>
        <ErrorBoundary fallback={<TabErrorFallback tabName="Profile" onReport={() => handleOpenFeedback('bug_report', 'error')} />}>
          <ProfilePanel />
        </ErrorBoundary>
        <ErrorBoundary fallback={<TabErrorFallback tabName="Audit" onReport={() => handleOpenFeedback('bug_report', 'error')} />}>
          <Suspense fallback={<TabFallback />}>
            <AuditLog />
          </Suspense>
        </ErrorBoundary>
        <hr className={styles.settingsDivider} />
        <button
          type="button"
          className={styles.aboutButton}
          onClick={() => setShowAbout(true)}
        >
          About
        </button>
      </SettingsDrawer>
      <AboutModal
        isOpen={showAbout}
        onClose={() => setShowAbout(false)}
        config={aboutConfig}
      />
      <FeedbackForm
        isOpen={feedbackOpen}
        onClose={handleCloseFeedback}
        initialType={feedbackConfig.type}
        initialCategory={feedbackConfig.category}
        initialError={feedbackConfig.error}
      />
      <FeedbackButton onOpen={handleOpenFeedback} />
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={
        <div className={styles.appFallback}>
          <div className={styles.appFallbackText}>Loading…</div>
        </div>
      }>
        <AppInner />
      </Suspense>
    </ErrorBoundary>
  );
}
